import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL || '';

/**
 * DncTab — central Do-Not-Contact list for the firm. Lists every candidate
 * with any DNC flag set (SMS / email / phone / all) and lets a recruiter
 * remove a candidate from a specific list (e.g. undo an accidental STOP).
 * Writes go through the same /api/outreach/dnc/:candidateId endpoint the
 * candidate profile's Contacts & Links widget uses, so state stays in sync.
 */
export default function DncTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all'); // all | sms | email | phone
  const [busyId, setBusyId] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/outreach/dnc`, { headers });
      if (r.ok) setRows(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  // Remove one channel's DNC for a candidate (or all).
  const removeDnc = async (candidateId, field) => {
    setBusyId(`${candidateId}:${field}`);
    try {
      const body = field === 'all'
        ? { dnc_all: false, dnc_sms: false, dnc_email: false, dnc_phone: false }
        : { [field]: false };
      const r = await fetch(`${API}/api/outreach/dnc/${candidateId}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      if (r.ok) await load();
      else alert('Failed to update DNC');
    } catch (e) { alert('Failed to update DNC'); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (channelFilter !== 'all') out = out.filter(r => r[`dnc_${channelFilter}`] || r.dnc_all);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(r => (r.candidate_name || '').toLowerCase().includes(q)
        || (r.email || '').toLowerCase().includes(q) || (r.phone || '').includes(q));
    }
    return out;
  }, [rows, channelFilter, search]);

  const Badge = ({ active, label, reason }) => active ? (
    <span title={reason || undefined} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-50 text-red-600 border border-red-200">{label}</span>
  ) : null;

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-2 flex-wrap px-3 pt-2 pb-2">
        <div className="relative">
          <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, phone..."
            className="h-[30px] w-56 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400" />
        </div>
        <div className="inline-flex h-[30px] border border-gray-300 rounded overflow-hidden bg-white">
          {[
            { key: 'all', label: 'All' },
            { key: 'sms', label: 'SMS' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
          ].map((t, i) => (
            <button key={t.key} onClick={() => setChannelFilter(t.key)}
              className={`px-3 text-sm transition-colors ${i > 0 ? 'border-l border-gray-300' : ''} ${
                channelFilter === t.key ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}>{t.label}</button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{filtered.length} on Do-Not-Contact</span>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-3 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
            <p className="text-gray-500 font-medium">No one on the Do-Not-Contact list</p>
            <p className="text-sm text-gray-400 mt-1">Candidates who opt out (reply STOP, unsubscribe, or are toggled off) appear here.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {filtered.map(r => (
              <div key={r.candidate_id} className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{r.candidate_name || 'Unknown'}</div>
                  <div className="text-[11px] text-gray-400 truncate">
                    {[r.current_title, r.current_company].filter(Boolean).join(' · ')}
                    {(r.email || r.phone) && <span className="text-gray-300"> · </span>}
                    {r.email || r.phone}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Badge active={r.dnc_all} label="All" reason={r.dnc_all_reason} />
                  <Badge active={r.dnc_sms} label="SMS" reason={r.dnc_sms_reason} />
                  <Badge active={r.dnc_email} label="Email" reason={r.dnc_email_reason} />
                  <Badge active={r.dnc_phone} label="Phone" reason={r.dnc_phone_reason} />
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                  {r.dnc_all ? (
                    <button onClick={() => removeDnc(r.candidate_id, 'all')} disabled={busyId === `${r.candidate_id}:all`}
                      className="text-[10px] text-emerald-700 hover:text-emerald-800 px-2 py-1 border border-emerald-200 rounded hover:bg-emerald-50 font-medium disabled:opacity-50">
                      {busyId === `${r.candidate_id}:all` ? '…' : 'Remove all'}
                    </button>
                  ) : (
                    ['sms', 'email', 'phone'].filter(ch => r[`dnc_${ch}`]).map(ch => (
                      <button key={ch} onClick={() => removeDnc(r.candidate_id, `dnc_${ch}`)} disabled={busyId === `${r.candidate_id}:dnc_${ch}`}
                        className="text-[10px] text-emerald-700 hover:text-emerald-800 px-2 py-1 border border-emerald-200 rounded hover:bg-emerald-50 font-medium disabled:opacity-50">
                        {busyId === `${r.candidate_id}:dnc_${ch}` ? '…' : `Allow ${ch.toUpperCase()}`}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
